import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "#components/atoms/Button/Button";
import { Text } from "#components/atoms/Typographies/Text";
import { Modal } from "#components/molecules/Modal/Modal";
import { Icon } from "#shared/icons/Icon";
import { FileButton } from "#shared/view/designSystem/FileButton";

const ACCEPTED_FILE_TYPES =
  ".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type ImportMaterialListButtonProps = {
  existingMaterialCount: number;
  loading: boolean;
  onImport: (file: File) => void;
};

export const ImportMaterialListButton = ({
  existingMaterialCount,
  loading,
  onImport,
}: ImportMaterialListButtonProps) => {
  const { t } = useTranslation();
  const [fileToConfirm, setFileToConfirm] = useState<File | null>(null);

  const handleFileSelected = (file: File | null) => {
    if (!file) {
      return;
    }
    if (existingMaterialCount > 0) {
      setFileToConfirm(file);
      return;
    }
    onImport(file);
  };

  const handleConfirm = () => {
    if (fileToConfirm) {
      onImport(fileToConfirm);
    }
    setFileToConfirm(null);
  };

  return (
    <>
      <FileButton onChange={handleFileSelected} accept={ACCEPTED_FILE_TYPES} disabled={loading}>
        {(props) => (
          <Button
            {...props}
            variant="secondary"
            size="md"
            loading={loading}
            leftSection={<Icon name="import" size={16} />}
          >
            {t("onboardingV2.materialImport.button")}
          </Button>
        )}
      </FileButton>
      <Modal
        opened={fileToConfirm !== null}
        title={t("onboardingV2.materialImport.confirmReplace.title")}
        primaryText={t("onboardingV2.materialImport.confirmReplace.confirm")}
        closeText={t("onboardingV2.materialImport.confirmReplace.cancel")}
        onClose={() => setFileToConfirm(null)}
        onPrimary={handleConfirm}
      >
        <Text size="small">
          {t("onboardingV2.materialImport.confirmReplace.message", {
            count: existingMaterialCount,
          })}
        </Text>
      </Modal>
    </>
  );
};
